import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import * as Sentry from "@sentry/react";
import store, { persistor } from "./redux/store";
import App from "./App";
import "./index.css";
import { registerSW } from "virtual:pwa-register";
import { StatusProvider } from "./components/template/StatusBar/StatusBar";
import { initSentry } from "./utils/sentry";

function getUserInfo() {
  const state = store.getState();
  return state.auth;
}

// Initialize Sentry
initSentry();

// Capture global errors
window.onerror = function (message, source, lineno, colno, error) {
  const userInfo = getUserInfo();
  const errorDetails = error || new Error(String(message));
  
  // Set user context for Sentry
  if (userInfo) {
    Sentry.setUser({
      id: userInfo?.userId || '',
      email: userInfo?.userName || '',
      username: userInfo?.userName || '',
    });
    
    // Set additional context
    Sentry.setContext("user_details", {
      userId: userInfo.userId,
      userName: userInfo.userName,
      tenant: userInfo.tenant,
    });
    
    // Add tags for better filtering
    Sentry.setTag("tenant", userInfo.tenant);
    Sentry.setTag("userId", userInfo.userId);
  }
  
  // Add breadcrumb for error location
  Sentry.addBreadcrumb({
    message: 'Global error caught',
    level: 'error',
    data: {
      source,
      lineno,
      colno,
      message: String(message)
    }
  });
  
  // Capture the error
  Sentry.captureException(errorDetails);
};

// Capture unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  const rejectionReason = event.reason || "Unknown rejection reason";
  const userInfo = getUserInfo();
  
  if (userInfo) {
    Sentry.setUser({
      id: userInfo?.userId || "",
      email: userInfo?.userName || "",
      username: userInfo?.userName || "",
    });
    
    Sentry.setContext("user_details", {
      userId: userInfo.userId,
      userName: userInfo.userName,
      tenant: userInfo.tenant,
    });
    
    Sentry.setTag("tenant", userInfo.tenant);
    Sentry.setTag("userId", userInfo.userId);
  }
  
  // Add breadcrumb for unhandled rejection
  Sentry.addBreadcrumb({
    message: 'Unhandled promise rejection',
    level: 'error',
    data: {
      reason: String(rejectionReason)
    }
  });
  
  // Create and capture error
  const error = new Error(`Unhandled rejection: ${rejectionReason}`);
  error.name = 'UnhandledPromiseRejection';
  Sentry.captureException(error);
});

// Handle service worker messages
navigator.serviceWorker.addEventListener("message", (event) => {
  if (event.data && event.data.type === "CACHE_COMPLETE") {
    // Add breadcrumb for cache completion
    Sentry.addBreadcrumb({
      message: 'Service worker cache complete',
      level: 'info',
      category: 'sw'
    });
    
    setTimeout(() => {
      window.alert("App is ready for offline use.");
      window.location.reload();
    }, 5000);
  }
  
  if (event.data?.type === "SENTRY_ERROR") {
    const { message, additionalData, level = 'error', fingerprint } = event.data.error;
    const userInfo = getUserInfo();

    // Set user context
    if (userInfo) {
      Sentry.setUser({
        id: userInfo?.userId || "",
        username: userInfo?.userName || "",
        email: userInfo?.userName || "",
      });
      
      Sentry.setContext("user_details", {
        userId: userInfo.userId,
        userName: userInfo.userName,
        tenant: userInfo.tenant,
      });
      
      Sentry.setTag("tenant", userInfo.tenant);
      Sentry.setTag("userId", userInfo.userId);
    }

    // Add service worker context
    Sentry.setContext("service_worker", {
      source: 'service_worker',
      ...additionalData
    });
    
    // Add breadcrumb
    Sentry.addBreadcrumb({
      message: 'Error received from service worker',
      level: level,
      category: 'sw',
      data: additionalData
    });

    // Create error and capture
    const error = new Error(message);
    error.name = 'ServiceWorkerError';
    
    // Capture with additional options
    Sentry.captureException(error, {
      level: level,
      tags: {
        source: 'service_worker',
        ...(userInfo?.tenant && { tenant: userInfo.tenant })
      },
      contexts: {
        service_worker: {
          ...additionalData
        }
      },
      ...(fingerprint && { fingerprint: Array.isArray(fingerprint) ? fingerprint : [fingerprint] })
    });
    
    console.log("Logged error from Service Worker to Sentry:", message, additionalData);
  }
  
  // Handle legacy APM_ERROR messages for backward compatibility
  if (event.data?.type === "APM_ERROR") {
    console.warn("Received legacy APM_ERROR message. Consider updating service worker to use SENTRY_ERROR.");
    
    const { message, additionalData } = event.data.error;
    const userInfo = getUserInfo();

    if (userInfo) {
      Sentry.setUser({
        id: userInfo?.userId || "",
        username: userInfo?.userName || "",
        email: userInfo?.userName || "",
      });
      
      Sentry.setContext("user_details", {
        userId: userInfo.userId,
        userName: userInfo.userName,
        tenant: userInfo.tenant,
      });
      
      Sentry.setTag("tenant", userInfo.tenant);
      Sentry.setTag("userId", userInfo.userId);
    }

    // Add metadata to Sentry context
    if (additionalData) {
      Sentry.setContext("legacy_apm_data", additionalData);
    }
    
    Sentry.addBreadcrumb({
      message: 'Legacy APM error received from service worker',
      level: 'error',
      category: 'sw',
      data: additionalData
    });

    const error = new Error(message);
    error.name = 'LegacyServiceWorkerError';
    Sentry.captureException(error);
    
    console.log("Logged legacy APM error from Service Worker to Sentry:", message, additionalData);
  }
});

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <StatusProvider>
          <App />
        </StatusProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);

if ("serviceWorker" in navigator) {
  const updateSW = registerSW({
    onRegistered(swRegistration) {
      if (swRegistration) {
        // Add breadcrumb for service worker registration
        Sentry.addBreadcrumb({
          message: 'Service worker registered successfully',
          level: 'info',
          category: 'sw'
        });
        
        swRegistration.addEventListener("updatefound", () => {
          const newSW = swRegistration.installing;
          if (newSW) {
            newSW.onstatechange = () => {
              if (
                newSW.state === "installed" &&
                navigator.serviceWorker.controller
              ) {
                // Log service worker update
                Sentry.addBreadcrumb({
                  message: 'Service worker update found',
                  level: 'info',
                  category: 'sw',
                  data: {
                    state: newSW.state,
                    hasController: !!navigator.serviceWorker.controller
                  }
                });
                
                const isUpdate = window.confirm(
                  "New update available! Would you like to update?"
                );
                if (isUpdate) {
                  Sentry.addBreadcrumb({
                    message: 'User accepted service worker update',
                    level: 'info',
                    category: 'sw'
                  });
                  
                  newSW.postMessage({ type: "SKIP_WAITING" });
                  localStorage.removeItem("sw_prompted_for_reload");
                }
              }
            };
          }
        });
      }

      console.log("Service worker has been registered.");
      
      // Send sync queue message
      postMessage({
        type: "SYNC_QUEUE",
        payload: {
          message: "Sync queue",
        },
      });
    },
    onNeedRefresh() {
      Sentry.addBreadcrumb({
        message: 'Service worker needs refresh',
        level: 'info',
        category: 'sw'
      });
      
      const isUpdate = window.confirm(
        "New Update Available. Click OK to update"
      );
      if (isUpdate) {
        Sentry.addBreadcrumb({
          message: 'User accepted app refresh',
          level: 'info',
          category: 'sw'
        });
        updateSW(true);
      }
    },
    onOfflineReady() {
      Sentry.addBreadcrumb({
        message: 'App ready for offline use',
        level: 'info',
        category: 'sw'
      });
    },
    onRegisterError(error) {
      Sentry.captureException(error, {
        tags: {
          source: 'service_worker_registration'
        },
        contexts: {
          service_worker: {
            action: 'registration',
            error: error.message
          }
        }
      });
      console.error('Service worker registration failed:', error);
    }
  });
}